import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HolidayLHS from './ListingLHS';
import HolidayRHS from './ListingRHS';
import { getMMT_HLD_OBJ } from '../../../../common/utils/appUtils';
import { getQueryStr, isMobile } from '../../../../common/utils/browserUtils';
import { getAllUrlParams, scrollTo } from '../../../helpers/utils';
import {
  getPackagesAction,
  clearAllPackagesAction,
  pageLoadingAction,
} from '../../../redux/actions/listingPackagesActions';
import { getGroupMetaDataAction } from '../../../redux/actions/listingGroupActions';
import { getListingMetaDataAction } from '../../../redux/actions/listingMetadataActions';
import OmnitureUtils from '../../../../common/utils/Omniture/OmnitureUtils';
import { faOnClearAllFilter } from './analytics-services';

const ListingBody = ({
  headerDetail,
  seoFilters,
  listingFilters,
  groupPackagesMap,
  groupList,
  groupMetadataIsLoading,
  clearAllPackagesAction,
  getPackagesAction,
  getGroupMetaDataAction,
  getListingMetaDataAction,
  metadataIsLoading,
  blockerNotificationsCount = 0,
  pageLoadingAction,
  cruiseData,
  loadEventTriggered,
}) => {
  const [filterTabActive, setFilterTabActive] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const { fromSEO } = getMMT_HLD_OBJ();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  useEffect(() => {
    pageLoadingAction(true);
  }, []);

  const handleFilterTab = type => {
    setFilterTabActive(type);
  };

  /**
   * @description
   * Handles filter value change. Puts the new filter value into URL.
   * Api call is not handled here. The is a seperate useEffect which
   * calls the api on URL change.
   *
   * @param {Object} newFilterState - Object that conatins the values
   * @param {string} newFilterState.urlParam - url param of filter being updated
   * @param {string[]} newFilterState.values - Array of new values
   */
  const handleFilterChange = useCallback(
    newFilterState => {
      let urlParams = getAllUrlParams(search);
      urlParams = {
        ...urlParams,
        ...seoFilters,
        version: 3,
      };
      if (urlParams[`${newFilterState?.urlParam}`.toLowerCase()]) {
        urlParams[`${newFilterState?.urlParam}`.toLowerCase()] = null;
      }
      urlParams[newFilterState?.urlParam] = newFilterState.values.join('~');

      const queryString = getQueryStr(urlParams);
      if (fromSEO) {
        navigate(`?${queryString}`);
      } else {
        setFilterTabActive('');
        navigate(`/holidays/india/search?${queryString}`);
      }
    },
    [seoFilters, search, fromSEO]
  );

  const handleClearAllFilters = useCallback(() => {
    faOnClearAllFilter();
    let urlParams = getAllUrlParams(search);
    // pageLoadingAction(true);
    urlParams = {
      ...urlParams,
      ...seoFilters,
      version: 3,
    };

    listingFilters?.forEach(filter => {
      if (urlParams[filter?.urlParam]) {
        urlParams[filter?.urlParam] = '';
      }
      if (urlParams[`${filter?.urlParam}`.toLowerCase()]) {
        urlParams[`${filter?.urlParam}`.toLowerCase()] = '';
      }
    });

    const queryString = getQueryStr(urlParams);
    if (fromSEO) {
      navigate(`?${queryString}`);
    } else {
      setFilterTabActive('');
      navigate(`/holidays/india/search?${queryString}`);
    }
  }, [listingFilters, search, seoFilters]);

  const loadPackages = useCallback(
    groupId => {
      getPackagesAction({ groupId });
    },
    [getPackagesAction]
  );

  const handleTabSelect = useCallback(
    (newActiveTab, index) => {
      pageLoadingAction(true);
      // eslint-disable-next-line no-undef
      const rowObjStr = ssr.isOnClient ? window.localStorage.getItem('currentRow') : '';
      let rowObj = null;
      if (rowObjStr) {
        rowObj = JSON.parse(rowObjStr);
      }
      if (rowObj?.currentRow > 0) {
        OmnitureUtils.trackClickEvent({
          prop54: `Viewed_Package_${rowObj?.currentRow}`,
          prop1: `Viewed_${
            groupList?.length
              ? `Collection_${groupList?.findIndex(group => group?.id === rowObj?.activeTab?.id)}`
              : 'Listing Page'
          }`,
        });
      }
      scrollTo(0, () => {
        setActiveTab(newActiveTab);
        if (!groupPackagesMap?.[newActiveTab?.id]?.packages?.length) {
          loadPackages(newActiveTab?.id);
        }
      });
      OmnitureUtils.trackClickEvent({
        prop54: `Collection_${index}_${newActiveTab?.shortName}`,
      });
    },
    [groupPackagesMap]
  );

  useEffect(() => {
    if ((!activeTab || activeTab.id === 'listing') && groupList.length) {
      setActiveTab(groupList[0]);
    } else if (!activeTab && !metadataIsLoading && !groupMetadataIsLoading) {
      setActiveTab({ id: 'listing' });
    }
  }, [groupList, activeTab, metadataIsLoading, groupMetadataIsLoading]);

  const clearDataAndRefresh = useCallback(
    async activeTabId => {
      clearAllPackagesAction();
      await getListingMetaDataAction();
      await getGroupMetaDataAction();
      await loadPackages(activeTabId);
    },
    [loadPackages, clearAllPackagesAction, getGroupMetaDataAction, getListingMetaDataAction]
  );

  useEffect(() => {
    if (!groupMetadataIsLoading) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      // eslint-disable-next-line no-undef
      const rowObjStr = ssr.isOnClient ? window.localStorage.getItem('currentRow') : '';
      let rowObj = null;
      if (rowObjStr) {
        rowObj = JSON.parse(rowObjStr);
      }
      if (rowObj?.currentRow > 0 && loadEventTriggered) {
        OmnitureUtils.trackClickEvent({
          prop54: `Viewed_Package_${rowObj?.currentRow}`,
          prop1: `Viewed_${
            groupList?.length
              ? `Collection_${groupList?.findIndex(group => group?.id === rowObj?.activeTab?.id)}`
              : 'Listing Page'
          }`,
        });
      }
      if (!fromSEO || (activeTab?.id && fromSEO)) {
        clearDataAndRefresh(activeTab?.id);
      }
    }
  }, [search]);

  return (
    <div className="holidayBody">
      <div
        className={`holidayContainer listingWrap ${fromSEO ? 'seoListingWrap' : ''} ${
          cruiseData ? 'cruise-wrapper' : ''
        }`}
      >
        <HolidayRHS
          handleFilterChange={handleFilterChange}
          filterTabActive={filterTabActive}
          handleFilterTab={handleFilterTab}
          handleClearAllFilters={handleClearAllFilters}
          handleTabSelect={handleTabSelect}
          activeTabId={activeTab?.id}
          loadEventTriggered={loadEventTriggered}
          {...{ blockerNotificationsCount }}
          cruiseData={cruiseData}
        />
        {(filterTabActive === 'filter' || !isMobile()) && !cruiseData && (
          <HolidayLHS
            handleFilterChange={handleFilterChange}
            filterTabActive={filterTabActive}
            handleFilterTab={handleFilterTab}
            activeTabId={activeTab?.id}
            handleClearAllFilters={handleClearAllFilters}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ metadata, seo, groupMetadata, listingPackages }) => ({
  metadataIsLoading: metadata?.isLoading,
  headerDetail: metadata?.headerDetail,
  seoFilters: seo?.filters,
  listingFilters: metadata?.listingFilters || [],
  groupList: groupMetadata?.groupDetailsList,
  groupMetadataIsLoading: groupMetadata?.isLoading,
  groupPackagesMap: listingPackages?.packagesByGroupId,
  cruiseData: seo?.cruiseListing,
});

const mapDispatchToProps = {
  clearAllPackagesAction,
  getPackagesAction,
  getGroupMetaDataAction,
  getListingMetaDataAction,
  pageLoadingAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ListingBody);
